import { useCallback, useEffect, useState } from 'react';

import { Alert, Badge, Button, Card, HelpTip, Input, Switch, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui';
import { formatRelativeDate, formatDataHoraSegundos } from '@/lib/format-date';
import { api } from '@/lib/api';

import { AvisosDoGrupo } from './AvisosDoGrupo';
import { GruposDoWhatsApp, type Grupo } from './GruposDoWhatsApp';
import { ListaDeTrabalho } from './ListaDeTrabalho';
import { IaNaDescricao, type LinhaFilaIa } from './IaNaDescricao';
import { ConexaoDoBot } from './ConexaoDoBot';
import { RoteamentoWhatsApp } from './RoteamentoWhatsApp';
import { TetoDoDia } from './TetoDoDia';
import { PlantaoERecusas } from './PlantaoERecusas';
import { TrilhaDoWhatsapp } from './TrilhaDoWhatsapp';
// 🔴 #280: a outra metade da pergunta — o que CHEGOU (a Trilha é o que saiu).
import { OQueChegou } from './OQueChegou';
import { OuvidoDoBot } from './OuvidoDoBot';
import { PontosDeAtencaoCard } from './PontosDeAtencaoCard';
import { TesteDeTarefa } from './TesteDeTarefa';
import { nomeDoGrupo } from './grupo-nome';
import { EditorMensagem } from './EditorMensagem';

/**
 * O cartão do WhatsApp em Configurações › Integrações — irmão do Omie e do
 * Auvo, não seção nova.
 *
 * 🔴 **TUDO NASCE DESLIGADO**, e a tela diz isso em vez de parecer quebrada.
 * O pedido do Matheus foi literal: *"a rodada do whatsapp deve iniciar com
 * todas as funções desligadas e eu escolho pelo v2 o momento de ligar."*
 *
 * ⚠️ Mensagem enviada é irreversível E chega ao cliente. É a régua de risco
 * mais alta do projeto — mais alta que a escrita no Omie, porque o Omie se
 * corrige e o cliente já leu. Por isso o cartão mostra, para cada aviso, **o
 * que impede o envio agora**: um interruptor desligado sem explicação faz a
 * pessoa procurar o problema no lugar errado.
 */

interface Config {
  temCredencial: boolean;
  credencialAtualizadaEmIso: string | null;
  credencialAtualizadaPor: string | null;
  temChaveIa: boolean;
  grupoTesteId: string | null;
  grupoTesteNome: string | null;
  freioPuxado: boolean;
  freioMotivo: string | null;
  freioPor: string | null;
  freioEmIso: string | null;
  tetoDiario: number;
  tetoExtraHoje: number;
  tetoEfetivo: number;
  esperaEntreEnviosSeg: number;
  instabilidadeCarenciaMin: number;
  /** 🔴 A janela de silêncio (frente 12): 24h é o piso e o padrão. */
  janelaSilencioHoras: number;
  enviadasHoje: number;
  iaLigada: boolean;
  iaOrientacao: string;
  iaFilaAvisoMin: number;
  iaRecusandoDesdeIso: string | null;
  iaRecusaMotivo: string | null;
  telefonePlantao?: string | null;
  comunicadosLigado: boolean;
}

interface Gatilho {
  chave: string;
  nome: string;
  descricao: string;
  ligado: boolean;
  v1AindaFaz: boolean;
  v1Desligado: boolean;
  temTemplate: boolean;
  soInterno: boolean;
  clientesLiberados: number;
  impedimento: string | null;
}

interface Status {
  config: Config;
  gatilhos: Gatilho[];
  /** 🔴 Só grupos internos da IGM podem ser o grupo de teste. */
  internos: Grupo[];
  fila: Grupo[];
  vinculados: Grupo[];
  todos: Grupo[];
  /** A fila da IA (mig 564) — esperando + resolvidas recentes. */
  filaIa: LinhaFilaIa[];
}

const AJUDA_DESLIGADO =
  'Nenhum aviso do WhatsApp sai enquanto você não ligar, um por um. Isso não é um defeito ' +
  'nem uma configuração pela metade: mensagem enviada não volta atrás e chega direto no ' +
  'grupo do condomínio, então quem decide o momento é você.';

const AJUDA_FREIO =
  'O freio para na hora todas as mensagens de WhatsApp e os e-mails a cliente, sem precisar mexer em mais nada. ' +
  'Os e-mails da equipe continuam saindo. Soltar o freio ' +
  'não liga nenhum aviso de volta — cada um continua exatamente como estava. Use sem medo se ' +
  'algo sair errado.';

const AJUDA_COMUNICADOS =
  'O comunicado é a mensagem que você escreve à mão e manda para vários grupos de uma vez — ' +
  'um aviso de feriado, uma mudança de telefone. Este interruptor libera o envio; ele nasce ' +
  'desligado de propósito, porque um comunicado errado chega a dezenas de condomínios em ' +
  'minutos. Ligar aqui não manda nada: escrever e disparar continuam sendo dois gestos, na ' +
  'tela de Comunicados.';

const AJUDA_GRUPO_TESTE =
  'O grupo que recebe as mensagens de teste. Ele não vem preenchido de propósito: no sistema ' +
  'antigo, o grupo usado como teste é o mesmo que está ligado a um cliente — e o teste iria ' +
  'para esse cliente. Escolha um grupo interno da IGM.';

function quando(iso: string | null | undefined): string {
  if (!iso) return 'nunca';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 'nunca' : formatDataHoraSegundos(d);
}

export function CartaoWhatsApp() {
  const [status, setStatus] = useState<Status | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const [url, setUrl] = useState('');
  const [instancia, setInstancia] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [erroCampo, setErroCampo] = useState<{ campo: string; texto: string } | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [motivoFreio, setMotivoFreio] = useState('');
  const [editando, setEditando] = useState<string | null>(null);
  const [avisosDe, setAvisosDe] = useState<string | null>(null);
  const [sincronizando, setSincronizando] = useState(false);

  const carregar = useCallback(() => {
    setErroCarga(null);
    setCarregando(true);
    api
      .get<Status>('/api/v1/admin/whatsapp/status')
      .then(setStatus)
      .catch((e: Error) => setErroCarga(e.message))
      .finally(() => setCarregando(false));
  }, []);

  useEffect(carregar, [carregar]);

  // ⚠️ Aqui havia uma busca da lista INTEIRA de clientes, para alimentar o
  // seletor de vínculo lá embaixo. Ela pedia `/api/v1/clientes-v2` — endereço
  // que não existe — e engolia o 404 num `.catch` que devolvia lista vazia.
  // 🔴 Resultado: o "Trocar cliente" do #218 abria vazio para sempre. Quem
  // escolhe agora é o `<ClienteV2Combobox>`, que procura no servidor sozinho.

  function falha(err: unknown, campoPadrao = 'geral') {
    const e = err as Error & { response?: { campo?: string; erro?: string } };
    const campo = e.response?.campo ?? campoPadrao;
    const texto = e.response?.erro ?? e.message;
    if (campo === 'geral') setErro(texto);
    else {
      setErroCampo({ campo, texto });
      document.getElementById(`wa-${campo}`)?.focus();
    }
  }

  async function salvarCredencial(e: React.FormEvent) {
    e.preventDefault();
    setErroCampo(null);
    setAviso(null);
    setErro(null);

    // ⚠️ FORMULÁRIO NUNCA É SURDO: erro no CAMPO, com foco — não só um toast.
    if (!url.trim()) {
      setErroCampo({ campo: 'url', texto: 'Informe o endereço da Evolution.' });
      document.getElementById('wa-url')?.focus();
      return;
    }
    if (!instancia.trim()) {
      setErroCampo({ campo: 'instancia', texto: 'Informe o nome da instância.' });
      document.getElementById('wa-instancia')?.focus();
      return;
    }
    if (!apiKey.trim()) {
      setErroCampo({ campo: 'apiKey', texto: 'Cole a chave da Evolution.' });
      document.getElementById('wa-apiKey')?.focus();
      return;
    }

    setSalvando(true);
    try {
      await api.post('/api/v1/admin/whatsapp/credencial', {
        url: url.trim(),
        instancia: instancia.trim(),
        apiKey: apiKey.trim(),
      });
      // ⚠️ Limpos no sucesso: o valor não fica na tela e não há como lê-lo de volta.
      setUrl('');
      setInstancia('');
      setApiKey('');
      setAviso('Chave guardada e cifrada. Nada foi ligado — os avisos continuam desligados.');
      carregar();
    } catch (err) {
      falha(err, 'url');
    } finally {
      setSalvando(false);
    }
  }

  /**
   * 🔴 O interruptor do disparo em massa (adendo 11/09). A função no servidor
   * existia desde a mig 542 e nunca teve rota nem botão: a tela de Comunicados
   * mandava ligar aqui, e aqui não havia o que ligar.
   */
  async function alternarComunicados(ligado: boolean) {
    setErro(null);
    try {
      await api.post('/api/v1/admin/whatsapp/comunicados-ligado', { ligado });
      setAviso(
        ligado
          ? 'Disparo em massa liberado. Nada foi enviado — os comunicados continuam esperando o gesto de disparar.'
          : 'Disparo em massa desligado. Os rascunhos continuam guardados.',
      );
      carregar();
    } catch (err) {
      falha(err);
    }
  }

  async function alternarFreio(puxar: boolean) {
    setErro(null);
    try {
      await api.post('/api/v1/admin/whatsapp/freio', { puxar, motivo: motivoFreio });
      setMotivoFreio('');
      setAviso(
        puxar
          ? 'Freio puxado. Nenhuma mensagem sai até você soltar.'
          : 'Freio solto. Nenhum aviso foi religado — cada um continua como estava.',
      );
      carregar();
    } catch (err) {
      falha(err);
    }
  }

  async function alternarGatilho(chave: string, ligado: boolean) {
    setErro(null);
    setAviso(null);
    try {
      await api.post(`/api/v1/admin/whatsapp/gatilhos/${chave}/ligado`, { ligado });
      carregar();
    } catch (err) {
      falha(err);
    }
  }

  async function marcarV1(chave: string, desligado: boolean) {
    setErro(null);
    try {
      await api.post(`/api/v1/admin/whatsapp/gatilhos/${chave}/v1-desligado`, { desligado });
      carregar();
    } catch (err) {
      falha(err);
    }
  }

  async function importar() {
    setImportando(true);
    setErro(null);
    setAviso(null);
    try {
      const r = await api.post<{ detalhe: string }>('/api/v1/admin/whatsapp/grupos/importar', {});
      setAviso(r.detalhe);
      carregar();
    } catch (err) {
      falha(err);
    } finally {
      setImportando(false);
    }
  }

  async function escolherGrupoTeste(grupoId: string) {
    setErro(null);
    try {
      await api.post('/api/v1/admin/whatsapp/grupo-teste', { grupoId });
      setAviso('Grupo de teste escolhido. Agora o botão "Enviar teste" de cada aviso funciona.');
      carregar();
    } catch (err) {
      falha(err);
    }
  }

  async function soltarGrupoTeste() {
    setErro(null);
    try {
      await api.delete('/api/v1/admin/whatsapp/grupo-teste');
      setAviso('Grupo de teste solto. Escolha outro quando quiser.');
      carregar();
    } catch (err) {
      falha(err);
    }
  }

  /** 🔴 A lista vinda da Evolution — o nome que aparece no WhatsApp. */
  async function sincronizarComEvolution() {
    setSincronizando(true);
    setErro(null);
    setAviso(null);
    try {
      const r = await api.post<{ detalhe: string }>('/api/v1/admin/whatsapp/grupos/sincronizar', {});
      setAviso(r.detalhe);
      carregar();
    } catch (err) {
      falha(err);
    } finally {
      setSincronizando(false);
    }
  }

  const cfg = status?.config;
  const ligados = status?.gatilhos.filter((g) => g.ligado).length ?? 0;

  const selo = cfg?.freioPuxado
    ? { rotulo: 'Freio puxado', variant: 'danger' as const }
    : !cfg?.temCredencial
      ? { rotulo: 'Esperando a chave', variant: 'neutral' as const }
      : ligados === 0
        ? { rotulo: 'Tudo desligado', variant: 'warning' as const }
        : {
            rotulo: `${ligados} de ${status?.gatilhos.length ?? 0} ligados`,
            variant: 'success' as const,
          };

  return (
    <Card className="p-4 sm:p-5 space-y-4" data-testid="integracao-whatsapp">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-xl text-ink-1 dark:text-ink-dark-1">WhatsApp</h2>
          <p className="text-xs text-ink-3 dark:text-ink-dark-3">
            Avisos automáticos nos grupos dos condomínios — cada um ligado por você, quando quiser.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <HelpTip texto={AJUDA_DESLIGADO} />
          <Badge variant={selo.variant} size="sm" data-testid="wa-estado">
            {selo.rotulo}
          </Badge>
        </div>
      </div>

      {carregando && <p className="text-sm text-ink-3 dark:text-ink-dark-3">Consultando…</p>}
      {erroCarga && <Alert variant="danger">{erroCarga}</Alert>}
      {erro && <Alert variant="danger" data-testid="wa-erro">{erro}</Alert>}
      {aviso && <Alert variant="info" data-testid="wa-aviso">{aviso}</Alert>}

      {cfg && status && (
        <>
          {/* ─── O estado de quem acabou de subir ─── */}
          {ligados === 0 && !cfg.freioPuxado && (
            <Alert variant="info" data-testid="wa-tudo-desligado">
              Nenhum aviso está ligado. É assim que esta tela nasce, de propósito — nenhuma
              mensagem sai enquanto você não ligar cada um. Não há nada quebrado aqui.
            </Alert>
          )}

          {/* ─── 🔴 O FREIO ─── */}
          <div
            className="rounded-lg border border-line p-3 dark:border-line-dark"
            data-testid="wa-freio"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                    Freio de emergência
                  </span>
                  <HelpTip texto={AJUDA_FREIO} />
                </div>
                <p className="text-xs text-ink-3 dark:text-ink-dark-3">
                  {cfg.freioPuxado
                    ? /**
                       * 🔴 22/09: HÁ QUANTO TEMPO, não só quando (decisão do Matheus). O freio é exceção CURTA, não estado — e ele para
                       * tudo: com ele puxado, o síndico que pedir reset de senha no Portal não recebe o e-mail.
                       */
                      `Puxado ${formatRelativeDate(cfg.freioEmIso ?? '')} por ${cfg.freioPor ?? 'alguém'} (${quando(cfg.freioEmIso)}). ${cfg.freioMotivo ?? ''}`
                    : /* 22/10/2026 (D-D): o e-mail da equipe sai sempre; o freio segura o WhatsApp e o e-mail a cliente. */
                      'Para na hora o WhatsApp e o e-mail a cliente, inclusive o reset de senha do Portal. Os e-mails da equipe continuam saindo. Soltar depois não religa nada.'}
                </p>
              </div>
              {cfg.freioPuxado ? (
                <Button size="sm" variant="secondary" onClick={() => void alternarFreio(false)} data-testid="wa-soltar-freio">
                  Soltar o freio
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Input
                    id="wa-motivo-freio"
                    value={motivoFreio}
                    onChange={(e) => setMotivoFreio(e.target.value)}
                    placeholder="Motivo (opcional)"
                    className="w-48"
                  />
                  <Button size="sm" variant="destructive" onClick={() => void alternarFreio(true)} data-testid="wa-puxar-freio">
                    Parar tudo
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* 🔴 A CONEXÃO (09/10/2026): acima das abas, como o freio — quando o bot cai, é o que se procura. */}
          <ConexaoDoBot />

          {/*
            🔴 REORGANIZADO em 10/09 (pedido do Matheus: "a tela está muito
            extensa e bagunçada"). O FREIO fica SEMPRE à vista, acima das abas
            — emergência não se procura em aba. O resto se divide pelo assunto:
            Avisos · Grupos · Quem recebe · IA · Conexão e limites. Nenhuma
            lógica mudou — só onde cada coisa mora na tela.
          */}
          <Tabs defaultValue="avisos">
            <TabsList>
              <TabsTrigger value="avisos">Os avisos</TabsTrigger>
              <TabsTrigger value="grupos">Grupos</TabsTrigger>
              {/* 🔴 A PORTA da lista de trabalho (13/09). Ela vive ao lado de
                  Grupos porque é a MESMA pergunta vista pelo avesso: ali estão
                  os 53; aqui, os que ainda não têm o bot — 49 de 53, que são
                  528 das 689 combinações sem receber nada. ⚠️ Quinta vez na
                  semana que uma função nasceu sem caminho; esta nasce com um. */}
              <TabsTrigger value="trabalho">Falta o bot</TabsTrigger>
              <TabsTrigger value="roteamento">Quem recebe</TabsTrigger>
              <TabsTrigger value="ia">IA na descrição</TabsTrigger>
              <TabsTrigger value="testar">Testar OS</TabsTrigger>
              <TabsTrigger value="conexao">Conexão e limites</TabsTrigger>
              {/* 🔴 A TRILHA GANHA PORTA (adendo 12/09). Ela existia desde
                  11/09, mas no FIM da aba mais longa — o Cowork procurou nas
                  seis abas, nos onze itens de Integrações e nos nove de
                  Sistema, e não achou. Tela sem porta visível é o #239/#261,
                  e desta vez acontecia com a tela feita para diagnosticar
                  esse tipo de defeito. */}
              <TabsTrigger value="trilha">Trilha de mensagens</TabsTrigger>
              {/* 🔴 E A TERCEIRA VEZ NA SEMANA (#280, 12/09). O cartão do
                  ouvido mandava ver o detalhe "na aba Trilha de mensagens" — e
                  a Trilha mostra o que SAIU. O que CHEGOU tinha endpoint desde
                  a mig 588 e nenhuma tela. ⚠️ Aba própria, ao lado da Trilha:
                  são as duas metades da mesma pergunta. */}
              <TabsTrigger value="chegou">O que chegou</TabsTrigger>
            </TabsList>

            <TabsContent value="conexao">
            <div className="space-y-4">
          {/* ─── A credencial ─── */}
          <form onSubmit={salvarCredencial} className="space-y-3" data-testid="wa-form-credencial">
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label htmlFor="wa-url" className="mb-1.5 block text-sm text-ink-2 dark:text-ink-dark-2">
                  Endereço da Evolution
                </label>
                <Input
                  id="wa-url"
                  autoComplete="off"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  error={erroCampo?.campo === 'url' ? erroCampo.texto : undefined}
                  placeholder={cfg.temCredencial ? 'Guardado — preencha só para trocar' : 'https://…'}
                />
              </div>
              <div>
                <label htmlFor="wa-instancia" className="mb-1.5 block text-sm text-ink-2 dark:text-ink-dark-2">
                  Instância
                </label>
                <Input
                  id="wa-instancia"
                  autoComplete="off"
                  value={instancia}
                  onChange={(e) => setInstancia(e.target.value)}
                  error={erroCampo?.campo === 'instancia' ? erroCampo.texto : undefined}
                  placeholder={cfg.temCredencial ? 'Guardada' : 'nome da instância'}
                />
              </div>
              <div>
                <label htmlFor="wa-apiKey" className="mb-1.5 block text-sm text-ink-2 dark:text-ink-dark-2">
                  Chave (API key)
                </label>
                <Input
                  id="wa-apiKey"
                  type="password"
                  autoComplete="off"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  error={erroCampo?.campo === 'apiKey' ? erroCampo.texto : undefined}
                  placeholder={cfg.temCredencial ? 'Guardada — preencha só para trocar' : 'Cole aqui'}
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" size="sm" loading={salvando} data-testid="wa-salvar-credencial">
                Guardar
              </Button>
              <span className="text-xs text-ink-3 dark:text-ink-dark-3">
                {/* ⚠️ Auditoria 11/09: "Guardada" afirmava o que não tinha
                    verificado — o selo só sabe que há valor no banco, nunca se
                    a chave funciona. O texto passa a dizer exatamente isso; a
                    prova de que funciona é o histórico de mensagens. */}
                {cfg.temCredencial
                  ? `Guardada e cifrada em ${quando(cfg.credencialAtualizadaEmIso)}. Isto não testa a conexão — quem mostra se as mensagens estão saindo é o histórico, aqui embaixo.`
                  : 'A chave fica cifrada no cofre. Guardar não liga nenhum aviso.'}
              </span>
            </div>
          </form>

          {/* ─── 🔴 O teto — trava de verdade, com liberação do dia ─── */}
          <TetoDoDia cfg={cfg} onMudou={carregar} />

          {/* 🔴 O DISPARO EM MASSA (adendo 11/09) — o interruptor que a tela de
              Comunicados mandava procurar aqui e que nunca existiu. */}
          <div
            className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4 dark:border-line-dark"
            data-testid="wa-comunicados-bloco"
          >
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                  Disparo em massa (Comunicados)
                </span>
                <HelpTip texto={AJUDA_COMUNICADOS} />
              </div>
              <p className="text-xs text-ink-3 dark:text-ink-dark-3">
                {cfg.comunicadosLigado
                  ? 'Liberado. Escrever e disparar continuam sendo dois gestos, na tela de Comunicados.'
                  : 'Desligado. Dá para escrever e guardar rascunhos; nenhum comunicado sai até você liberar aqui.'}
              </p>
            </div>
            <Switch
              checked={cfg.comunicadosLigado}
              label={cfg.comunicadosLigado ? 'Liberado' : 'Desligado'}
              onChange={(e) => void alternarComunicados(e.target.checked)}
              data-testid="wa-comunicados-ligado"
            />
          </div>

          {/* FRENTES 8/9 (11/09): o plantao com fonte + as recusas encontraveis. */}
          <div className="border-t border-line pt-4 dark:border-line-dark">
            <PlantaoERecusas telefonePlantao={cfg.telefonePlantao ?? null} onMudou={carregar} />
          </div>

          {/* 🔴 O OUVIDO DO BOT (#260, mig 588) — a porta de ENTRADA. Mora aqui
              porque é irmã da credencial e do freio: é configuração de conexão,
              não um aviso. */}
          <OuvidoDoBot onMudou={carregar} />

            </div>
            </TabsContent>

            {/* 🔴 A TRILHA, em aba própria: "por que o cliente X não recebeu?"
                é uma pergunta inteira, não um rodapé de outra tela. */}
            <TabsContent value="chegou">
              <OQueChegou />
            </TabsContent>

            <TabsContent value="trilha">
              <TrilhaDoWhatsapp
                grupos={(status?.todos ?? []).map((g: Grupo) => ({
                  grupoId: g.grupoId,
                  nome: g.nomeNaEvolution ?? g.nomeNoV1 ?? g.grupoId,
                }))}
              />
            </TabsContent>

            <TabsContent value="trabalho">
              <ListaDeTrabalho onMudou={carregar} />
            </TabsContent>

            <TabsContent value="grupos">
            <div className="space-y-4">
          {/* ─── 🔴 O GRUPO DE TESTE — só interno da IGM, e sempre trocável ─── */}
          <div data-testid="wa-grupo-teste">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">Grupo de teste</span>
              <HelpTip texto={AJUDA_GRUPO_TESTE} />
            </div>

            {cfg.grupoTesteId ? (
              <div className="mt-1 flex flex-wrap items-center gap-3">
                <span className="text-sm text-ink-2 dark:text-ink-dark-2">
                  {cfg.grupoTesteNome ?? cfg.grupoTesteId}
                </span>
                {/* 🔴 Trocar SEMPRE. A versao anterior travava depois da primeira escolha. */}
                <Button size="sm" variant="ghost" onClick={() => void soltarGrupoTeste()} data-testid="wa-trocar-grupo-teste">
                  Trocar
                </Button>
              </div>
            ) : (
              <div className="mt-2 space-y-2">
                {status.internos.length === 0 ? (
                  <Alert variant="warning" data-testid="wa-sem-interno">
                    Nenhum grupo está marcado como interno da IGM. Só um grupo interno pode receber
                    as mensagens de teste — marque o grupo da equipe na lista abaixo, com o botão
                    <strong> É da IGM</strong>.
                  </Alert>
                ) : (
                  <>
                    <p className="text-sm text-ink-2 dark:text-ink-dark-2">
                      Escolha entre os grupos internos da IGM. Grupo de cliente não aparece aqui —
                      a mensagem de teste iria para o condomínio.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {status.internos.map((g) => (
                        <Button
                          key={g.grupoId}
                          size="sm"
                          variant="secondary"
                          onClick={() => void escolherGrupoTeste(g.grupoId)}
                        >
                          {nomeDoGrupo(g)}
                        </Button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* ─── OS GRUPOS — tudo alterável, sempre ─── */}
          <div data-testid="wa-grupos">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                {/* ⬜ Concordância e o eixo que importa (auditoria 11/09): o
                    número que decide se a mensagem sai é o do BOT. */}
                Grupos ({status.todos.filter((g) => !g.foraDoBot && g.vistoNaEvolutionIso).length} com
                o bot · {status.vinculados.length} com cliente · {status.fila.length} na fila ·{' '}
                {status.internos.length} {status.internos.length === 1 ? 'interno' : 'internos'})
              </h3>
              <div className="flex flex-wrap gap-2">
                {/* 🔴 O nome que o Matheus reconhece vem daqui. */}
                <Button
                  size="sm"
                  loading={sincronizando}
                  onClick={() => void sincronizarComEvolution()}
                  data-testid="wa-sincronizar"
                  title="Pergunta ao WhatsApp em quais grupos o bot está, e traz o nome real de cada um. É também o gesto que reconhece um grupo READICIONADO: a partir desta conferência, os avisos daquele grupo valem — e nada antigo sai (aviso que não pôde ser entregue é descartado na hora, com o motivo na trilha)."
                >
                  Buscar grupos do WhatsApp
                </Button>
                <Button size="sm" variant="ghost" loading={importando} onClick={() => void importar()} data-testid="wa-importar">
                  Importar do sistema antigo
                </Button>
              </div>
            </div>

            <div className="mt-3">
              <GruposDoWhatsApp grupos={status.todos} onMudou={carregar} onAvisos={setAvisosDe} />
            </div>
          </div>
            </div>
            </TabsContent>

            <TabsContent value="avisos">
          {/* ─── OS AVISOS — os 6 de sempre + os 3 de instabilidade ─── */}
          <div>
            <h3 className="mb-2 text-sm font-medium text-ink-1 dark:text-ink-dark-1">
              Os avisos ({ligados} de {status.gatilhos.length} ligados)
            </h3>
            <div className="space-y-2">
              {status.gatilhos.map((g) => (
                <div
                  key={g.chave}
                  className="rounded-lg border border-line p-3 dark:border-line-dark"
                  data-testid={`wa-gatilho-${g.chave}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">{g.nome}</span>
                        {g.v1AindaFaz && !g.v1Desligado && (
                          <Badge variant="warning" size="sm" data-testid={`wa-v1-faz-${g.chave}`}>
                            O sistema antigo ainda faz
                          </Badge>
                        )}
                        {!g.temTemplate && (
                          <Badge variant="neutral" size="sm">Sem texto</Badge>
                        )}
                        {/* 🔴 Instabilidade (mig 558): só interno + a contagem
                            de quem foi liberado — a resposta a "quantos?". */}
                        {g.soInterno && (
                          <Badge variant="neutral" size="sm" data-testid={`wa-so-interno-${g.chave}`}>
                            {g.clientesLiberados === 0
                              ? 'Só o interno — nenhum cliente liberado'
                              : g.clientesLiberados === 1
                                ? 'Interno + 1 cliente liberado'
                                : `Interno + ${g.clientesLiberados} clientes liberados`}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-ink-3 dark:text-ink-dark-3">{g.descricao}</p>
                      {/* ⚠️ O motivo, sempre. Interruptor desligado sem explicação
                          faz a pessoa procurar o problema no lugar errado. */}
                      {g.impedimento && (
                        <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">{g.impedimento}</p>
                      )}
                    </div>
                    {/* ⚠️ `flex-wrap` e sem `shrink-0`: medido em 06/09, esta linha
                        tinha 442px de largura fixa num celular de 375 e criava
                        112px de rolagem horizontal na página. Os dois
                        interruptores quebram para a linha de baixo em vez de
                        empurrar a tela. */}
                    <div className="flex flex-wrap items-center gap-3">
                      {g.v1AindaFaz && (
                        <Switch
                          checked={g.v1Desligado}
                          onChange={(e) => void marcarV1(g.chave, e.target.checked)}
                          label="Já desliguei lá"
                          data-testid={`wa-v1-desligado-${g.chave}`}
                        />
                      )}
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setEditando(g.chave)}
                        data-testid={`wa-editar-${g.chave}`}
                      >
                        {g.temTemplate ? 'Ver a mensagem' : 'Escrever a mensagem'}
                      </Button>
                      {/* ⚠️ O rótulo diz o ESTADO, não o nome do interruptor.
                          "Ligado" fixo ao lado de um aviso desligado é a tela
                          afirmando o contrário do que ela mesma mostra — o
                          Matheus leu "Ligado" nos dois avisos de chamado, que
                          estão desligados. O interruptor de comunicados já
                          fazia certo; este não. */}
                      <Switch
                        checked={g.ligado}
                        onChange={(e) => void alternarGatilho(g.chave, e.target.checked)}
                        label={g.ligado ? 'Ligado' : 'Desligado'}
                        data-testid={`wa-ligar-${g.chave}`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

            </TabsContent>

            <TabsContent value="ia">
              {/* ─── 🔴 A IA na descrição + a fila que não pode ser silenciosa ─── */}
              <div className="space-y-4">
                <IaNaDescricao cfg={cfg} fila={status.filaIa} onMudou={carregar} />
                {/* FRENTE 5 (11/09): os pontos de atencao do V1, editaveis como a orientacao. */}
                <div className="border-t border-line pt-4 dark:border-line-dark">
                  <PontosDeAtencaoCard />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="testar">
              {/* FRENTE 6 (11/09): testar o resumo de uma OS pelo ID. */}
              <TesteDeTarefa />
            </TabsContent>

            <TabsContent value="roteamento">
              {/* ─── Quem recebe o quê + a simulação (Frente C) ─── */}
              <RoteamentoWhatsApp gatilhos={status.gatilhos} />
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* O editor de cada aviso — com previa e o unico botao que dispara. */}
      <AvisosDoGrupo
        grupoId={avisosDe}
        nomeDoGrupo={
          (avisosDe &&
            (() => {
              const g = status?.todos.find((x) => x.grupoId === avisosDe);
              return g ? (g.nomeNaEvolution ?? g.nomeNoV1) : null;
            })()) ||
          null
        }
        aberto={avisosDe !== null}
        onFechar={() => setAvisosDe(null)}
        onSalvou={carregar}
      />

      <EditorMensagem
        chave={editando}
        aberto={editando !== null}
        onFechar={() => setEditando(null)}
        onSalvou={carregar}
        temGrupoTeste={Boolean(cfg?.grupoTesteId)}
      />
    </Card>
  );
}
